/**
 * Reset password page — public, no auth required.
 *
 * Reads the token from the URL query string, validates it with the API,
 * then lets the user choose a new password.
 */

import { useState, useEffect, type FormEvent } from 'react';
import BrandTitle from '@/components/common/BrandTitle';
import BrandMark from '@/components/common/BrandMark';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { validateResetToken, resetPassword } from '@/api/users';

type State = 'loading' | 'valid' | 'invalid' | 'success';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [state, setState] = useState<State>('loading');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }

    validateResetToken(token)
      .then((data) => {
        setEmail(data.email);
        setState('valid');
      })
      .catch(() => {
        setState('invalid');
      });
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, newPassword);
      setState('success');
      // Redirect to login after short delay
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setError('Failed to reset password. The link may have expired or already been used.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Brand */}
        <div className="text-center">
          <BrandMark className="mx-auto h-14 w-14" />
          <BrandTitle/>
          <p className="mt-1 text-sm text-gray-500">Reset your password</p>
        </div>

        {state === 'loading' && (
          <div className="text-center text-sm text-gray-500">Validating your reset link…</div>
        )}

        {state === 'invalid' && (
          <div className="space-y-4 text-center">
            <div className="rounded-md bg-red-50 px-4 py-4">
              <p className="text-sm font-medium text-red-800">Link invalid or expired</p>
              <p className="mt-1 text-sm text-red-700">
                This password reset link is invalid, expired, or has already been used.
              </p>
            </div>
            <Link
              to="/forgot-password"
              className="block text-sm text-indigo-600 hover:text-indigo-800"
            >
              Request a new reset link
            </Link>
            <Link
              to="/login"
              className="block text-sm text-gray-500 hover:text-gray-700"
            >
              Back to login
            </Link>
          </div>
        )}

        {state === 'success' && (
          <div className="space-y-4 text-center">
            <div className="rounded-md bg-green-50 px-4 py-4">
              <p className="text-sm font-medium text-green-800">Password reset successfully</p>
              <p className="mt-1 text-sm text-green-700">
                Redirecting you to the login page…
              </p>
            </div>
            <Link
              to="/login"
              className="block text-sm text-indigo-600 hover:text-indigo-800"
            >
              Go to login
            </Link>
          </div>
        )}

        {state === 'valid' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {email && (
              <p className="text-sm text-gray-600">
                Resetting password for <strong>{email}</strong>
              </p>
            )}

            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="new-password"
                type="password"
                className="input-field mt-1"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input
                id="confirm-password"
                type="password"
                className="input-field mt-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your new password"
                required
                minLength={8}
              />
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary w-full">
              {isLoading ? 'Resetting…' : 'Reset Password'}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
